import { useMemo } from 'react';
import { useOrganization } from '@/hooks/use-organization';
import { BillingModel, BillingModelConfig } from '@/lib/billing/types';
import { BILLING_MODEL_CONFIGS, DEFAULT_BILLING_MODEL } from '@/lib/billing/configs';
import { getBillingModelConfig } from '@/lib/billing/utils';

export interface UseBillingModelReturn {
  /** The current billing model */
  model: BillingModel;
  /** Full configuration for the current model */
  config: BillingModelConfig;
  /** Whether organization data is still loading */
  loading: boolean;
  /** Whether using default billing model (not explicitly set) */
  isUsingDefault: boolean;
  /** Convenience accessors for feature flags */
  showPackages: boolean;
  showSessionsRemaining: boolean;
  showReupProjections: boolean;
  trackEarningsPerSession: boolean;
  requirePackageForSession: boolean;
  /** Convenience accessors for churn risk factors */
  churnFactors: {
    packageEndingSoon: boolean;
    noRenewalScheduled: boolean;
    subscriptionEndingSoon: boolean;
    paymentOverdue: boolean;
  };
}

/**
 * Hook to access the current organization's billing model and its configuration.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { showPackages, model } = useBillingModel();
 *
 *   return (
 *     <div>
 *       <p>Current model: {model}</p>
 *       {showPackages && <PackageSection />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useBillingModel(): UseBillingModelReturn {
  const { organization, loading } = useOrganization();

  return useMemo(() => {
    const savedModel = organization?.settings?.billing_model as BillingModel | undefined;
    const model = savedModel || DEFAULT_BILLING_MODEL;
    const isUsingDefault = !savedModel;
    const config = getBillingModelConfig(model);

    // Debug logging in development
    if (process.env.NODE_ENV === 'development' && organization && !loading) {
      if (isUsingDefault) {
        console.warn(
          '[BillingModel] No billing_model set in organization settings. Using default:',
          model,
          '\nTo fix: Go to Settings → Account → Billing Model and select your preferred model.'
        );
      }
    }

    return {
      model,
      config,
      loading,
      isUsingDefault,
      showPackages: config.features.showPackages,
      showSessionsRemaining: config.features.showSessionsRemaining,
      showReupProjections: config.features.showReupProjections,
      trackEarningsPerSession: config.features.trackEarningsPerSession,
      requirePackageForSession: config.features.requirePackageForSession,
      churnFactors: config.churnRiskFactors,
    };
  }, [organization?.settings?.billing_model, loading, organization]);
}
